import { Controller } from "@hotwired/stimulus"

// Polling fallback for rows on the exports list. A row normally updates via the
// Turbo Stream broadcast from ExportJob, but that broadcast is lost when the job
// finishes before the client's ActionCable subscription is confirmed — common for
// fast CSV exports right after the redirect to /exports — or during a disconnect,
// leaving the row stuck on "In Bearbeitung" until a manual refresh.
//
// While the export is in a transient state this controller polls the status
// endpoint passing the status it knows. The server replaces the row only when
// the status changed (otherwise 204). The controller lives inside the replaced
// row, so each swap re-instantiates it with the fresh status: it re-arms while
// transient and stays idle once a terminal state (completed/failed) is reached.
const TRANSIENT_STATES = ["pending", "processing"]

export default class extends Controller {
  static values = {
    url: String,
    status: String,
    interval: { type: Number, default: 4000 },
  }

  connect() {
    if (!TRANSIENT_STATES.includes(this.statusValue)) return

    this.boundVisibilityChange = this.handleVisibilityChange.bind(this)
    document.addEventListener("visibilitychange", this.boundVisibilityChange)
    this.scheduleTick()
  }

  disconnect() {
    this.clearTimer()
    if (this.boundVisibilityChange) {
      document.removeEventListener("visibilitychange", this.boundVisibilityChange)
    }
  }

  scheduleTick() {
    this.clearTimer()
    if (document.hidden) return

    this.timer = setTimeout(() => this.poll(), this.intervalValue)
  }

  clearTimer() {
    if (this.timer) clearTimeout(this.timer)
    this.timer = null
  }

  handleVisibilityChange() {
    if (document.hidden) {
      this.clearTimer()
    } else {
      this.poll()
    }
  }

  async poll() {
    let body = ""
    try {
      const url = new URL(this.urlValue, window.location.origin)
      url.searchParams.set("known_status", this.statusValue)
      const response = await fetch(url, {
        headers: { Accept: "text/vnd.turbo-stream.html" },
      })
      // 204 means unchanged — keep polling. A body means the row (and this
      // controller) is about to be replaced, so we don't schedule another tick.
      if (response.ok && response.status !== 204) {
        body = await response.text()
      }
    } catch {
      // Network hiccups are fine — re-arm and the next tick retries.
    }

    if (body) {
      Turbo.renderStreamMessage(body)
    } else {
      this.scheduleTick()
    }
  }
}
